"use client";

import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackFormSubmissionGTM } from '@/lib/analytics';
import { getStoredUTMParams } from '@/lib/utm';
import type { WhitepaperFormDeps } from './WhitepaperFormDependencies';

interface ToCItem {
    id: string;
    title: string;
}

interface WhitepaperToCProps {
    items: ToCItem[];
    campaignIdOverride?: string;
    whitepaperTitle?: string;
}

// The download form's third-party hooks (reCAPTCHA, Salesforce identity) are
// lazy-loaded on first form-field focus so no whitepaper view pays for them
// unless the visitor engages the form.
const WhitepaperFormDependencies = React.lazy(
    () => import('./WhitepaperFormDependencies'),
);

/**
 * WhitepaperToC Component
 *
 * A sticky sidebar component that provides navigation for Whitepaper sections.
 * Owns scroll-spy state via IntersectionObserver so the parent template can
 * render server-side while the active-section highlight stays accurate.
 */
export function WhitepaperToC({
    items,
    campaignIdOverride,
    whitepaperTitle,
}: WhitepaperToCProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeSectionId, setActiveSectionId] = useState<string>('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [formActivated, setFormActivated] = useState(false);
    const depsRef = useRef<WhitepaperFormDeps>({});

    const handleDepsReady = useCallback((deps: WhitepaperFormDeps) => {
        depsRef.current = deps;
    }, []);

    // Active-section highlight — IntersectionObserver replaces the parent's
    // unthrottled window scroll listener. The `-50% 0px -50% 0px` rootMargin
    // treats the viewport mid-line as the active threshold, matching the
    // previous "last section whose top crossed the midpoint" behaviour.
    useEffect(() => {
        if (typeof window === 'undefined' || items.length === 0) return;

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setActiveSectionId(entry.target.id);
                    }
                }
            },
            { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
        );

        const observed: Element[] = [];
        for (const item of items) {
            const el = document.getElementById(item.id);
            if (el) {
                observer.observe(el);
                observed.push(el);
            }
        }
        return () => {
            for (const el of observed) observer.unobserve(el);
            observer.disconnect();
        };
    }, [items]);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 100; // Account for sticky header
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    if (items.length === 0) return null;

    const validateEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const activateForm = () => {
        if (!formActivated) setFormActivated(true);
    };

    const handleDownloadSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const trimmedCompany = company.trim();
        if (!trimmedCompany) {
            setErrorMessage('Company Name is required');
            setFormState('error');
            return;
        }

        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setErrorMessage('Email is required');
            setFormState('error');
            return;
        }

        if (!validateEmail(trimmedEmail)) {
            setErrorMessage('Please enter a valid email address');
            setFormState('error');
            return;
        }

        setFormState('submitting');
        setErrorMessage('');

        const utmParams = getStoredUTMParams();
        const { executeRecaptcha, sendIdentityEvent } = depsRef.current;

        try {
            let recaptchaToken: string | undefined;
            if (executeRecaptcha) {
                try {
                    recaptchaToken = await executeRecaptcha('whitepaper_download');
                } catch (recaptchaError) {
                    console.warn('reCAPTCHA token generation failed:', recaptchaError);
                }
            }

            const response = await fetch('/api/forms/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    formType: 'whitepaper_download',
                    data: { email: trimmedEmail, company: trimmedCompany },
                    recaptchaToken,
                    pageUrl: window.location.href,
                    campaignIdOverride,
                    utmParams,
                }),
            });

            if (response.ok) {
                setFormState('success');
                if (sendIdentityEvent) {
                    sendIdentityEvent(trimmedEmail, 'whitepaper_download');
                }
                setEmail('');
                setCompany('');
                trackFormSubmissionGTM('whitepaper_download', true, {
                    asset_title: whitepaperTitle || 'Whitepaper',
                    ...(utmParams as unknown as Record<string, string>),
                });
            } else {
                const data = await response.json();
                setErrorMessage(data.message || 'Something went wrong. Please try again.');
                setFormState('error');
                trackFormSubmissionGTM('whitepaper_download', false, {
                    asset_title: whitepaperTitle || 'Whitepaper',
                    ...(utmParams as unknown as Record<string, string>),
                });
            }
        } catch {
            setErrorMessage('An unexpected error occurred. Please try again.');
            setFormState('error');
            trackFormSubmissionGTM('whitepaper_download', false, {
                asset_title: whitepaperTitle || 'Whitepaper',
                ...(utmParams as unknown as Record<string, string>),
            });
        }
    };

    return (
        <div className="sticky top-32 space-y-8 z-20">
            {/* Table of Contents */}
            <div>
                <div className="bg-white border-2 border-aq-off-white rounded-sm shadow-sm overflow-hidden transition-all duration-300">
                    {/* Header Toggle */}
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        aria-label="Toggle table of contents"
                        aria-expanded={isOpen}
                        className="w-full px-6 py-4 flex items-center justify-between group hover:bg-aq-off-white transition-colors"
                    >
                        <span className="text-sm font-bold text-foreground uppercase tracking-widest">
                            Table of Contents
                        </span>
                        <ChevronDown className={cn(
                            "w-5 h-5 text-primary transition-transform duration-300",
                            isOpen ? "rotate-180" : "rotate-0"
                        )} />
                    </button>

                    {/* Links List */}
                    <div className={cn(
                        "transition-all duration-300 ease-in-out border-t border-aq-off-white",
                        isOpen ? "max-h-[var(--aq-width-content-wide)] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
                    )}>
                        <nav aria-label="Table of contents" role="navigation" className="p-4 space-y-1">
                            {items.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => scrollToSection(item.id)}
                                    aria-label={`Navigate to section: ${item.title}`}
                                    aria-current={activeSectionId === item.id ? 'true' : undefined}
                                    className={cn(
                                        "w-full text-left px-4 py-2.5 rounded-sm text-sm transition-all duration-200 flex items-start gap-3",
                                        activeSectionId === item.id
                                            ? "bg-accent text-accent-foreground font-semibold shadow-md translate-x-1"
                                            : "text-gray-600 hover:bg-muted hover:text-foreground"
                                    )}
                                >
                                    <span className={cn(
                                        "w-1.5 h-1.5 rounded-full mt-1.5 transition-colors",
                                        activeSectionId === item.id ? "bg-primary" : "bg-transparent"
                                    )} />
                                    {item.title}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>

            {/* Lead Magnet */}
            <div>
                <div className="p-10 rounded-sm bg-accent text-accent-foreground text-center space-y-6 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-primary/20 transition-all duration-700" />
                    <p className="text-2xl font-bold relative z-10">Take it with you.</p>
                    <p className="text-sm text-white/70 relative z-10">Download the full PDF version of this whitepaper for offline reading.</p>
                    <div className="relative z-10 space-y-3">
                        {formState === 'success' ? (
                            <p className="text-sm text-white/80">
                                Thanks! You&apos;re all set.
                            </p>
                        ) : (
                            <form onSubmit={handleDownloadSubmit} aria-label="Download whitepaper" role="form" className="space-y-3">
                                <label htmlFor="wp-company" className="sr-only">Company Name</label>
                                <input
                                    id="wp-company"
                                    type="text"
                                    placeholder="Company Name"
                                    aria-label="Company Name"
                                    value={company}
                                    onChange={(e) => {
                                        setCompany(e.target.value);
                                        if (formState === 'error') setFormState('idle');
                                    }}
                                    onFocus={activateForm}
                                    disabled={formState === 'submitting'}
                                    autoComplete="organization"
                                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm disabled:opacity-50"
                                />
                                <label htmlFor="wp-email" className="sr-only">Email Address</label>
                                <input
                                    id="wp-email"
                                    type="email"
                                    placeholder="Email Address"
                                    aria-label="Email Address"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (formState === 'error') setFormState('idle');
                                    }}
                                    onFocus={activateForm}
                                    disabled={formState === 'submitting'}
                                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-sm disabled:opacity-50"
                                />
                                <button
                                    type="submit"
                                    aria-label="Download PDF"
                                    disabled={formState === 'submitting'}
                                    className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-sm hover:bg-primary/90 transition-all hover:scale-102 active:scale-98 uppercase tracking-widest text-sm shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    {formState === 'submitting' ? 'Submitting…' : 'Download PDF'}
                                </button>
                                {formState === 'error' && errorMessage && (
                                    <p className="text-red-300 text-xs text-left">{errorMessage}</p>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
            {formActivated && (
                <Suspense fallback={null}>
                    <WhitepaperFormDependencies onReady={handleDepsReady} />
                </Suspense>
            )}
        </div>
    );
}
